// src/helpers/SoapGenerator.ts

import moment from "moment";

import { bandNyeri, judulBandNyeri } from "@/utils/nyeriIntervensi";

export interface SoapData {
  s: string; // Subjective
  o: string; // Objective
  a: string; // Assessment
  p: string; // Plan
}

export class SoapGenerator {
  // ==================== Helpers ====================

  private static formatTtv(ttv: any): string {
    if (!ttv) return "";
    const parts: string[] = [];

    if (ttv.sistole && ttv.diastole)
      parts.push(`TD: ${ttv.sistole}/${ttv.diastole} mmHg`);
    if (ttv.frekuensi_nadi) parts.push(`Nadi: ${ttv.frekuensi_nadi} x/menit`);
    if (ttv.frekuensi_nafas)
      parts.push(`Nafas: ${ttv.frekuensi_nafas} x/menit`);
    if (ttv.suhu_badan) parts.push(`SB: ${ttv.suhu_badan} \u2070C`);
    if (ttv.saturasi_oksigen) parts.push(`SpO2: ${ttv.saturasi_oksigen}%`);
    if (ttv.keadaan_umum) parts.push(`KU: ${ttv.keadaan_umum}`);
    if (ttv.kesadaran) parts.push(`Kesadaran: ${ttv.kesadaran}`);

    return parts.length > 0 ? parts.join(", ") + ";" : "";
  }

  private static formatAntropometri(antro: any): string {
    if (!antro) return "";
    const parts: string[] = [];

    if (antro.berat_badan) parts.push(`BB: ${antro.berat_badan} kg`);
    if (antro.tinggi_badan) parts.push(`TB: ${antro.tinggi_badan} cm`);
    if (antro.berat_badan && antro.tinggi_badan) {
      parts.push(`IMT: ${this.setImt(antro.berat_badan, antro.tinggi_badan)}`);
    }
    if (antro.lingkar_kepala)
      parts.push(`Lingkar Kepala: ${antro.lingkar_kepala} cm`);
    if (antro.lla) parts.push(`LLA: ${antro.lla} cm`);

    return parts.length > 0 ? parts.join(", ") + ";" : "";
  }

  private static formatNyeri(nyeri: any, fallbackNyeri?: any): string {
    // Use fallback if primary nyeri exists but has no scale, or if primary is missing entirely
    const data = nyeri && nyeri.skala ? nyeri : fallbackNyeri;

    if (!data || !data.skala) return "";
    const parts: string[] = [`Skala Nyeri: ${data.skala}`];

    if (data.lokasi) parts.push(`Lokasi: ${data.lokasi}`);
    if (data.intensitas) parts.push(`Intensitas: ${data.intensitas}`);
    if (data.durasi) parts.push(`Durasi: ${data.durasi} detik`);
    if (data.frekuensi) parts.push(`Frekuensi: ${data.frekuensi} x/menit`);
    if (data.pencetus) parts.push(`Pencetus: ${data.pencetus}`);
    if (data.kualitas) parts.push(`Kualitas Nyeri: ${data.kualitas}`);
    if (data.pola_serangan) parts.push(`Pola Serangan: ${data.pola_serangan}`);
    if (data.penyebab_hilang)
      parts.push(`Penyebab Nyeri Hilang: ${data.penyebab_hilang}`);
    if (data.jenis) {
      const type =
        data.jenis == "1" ? "Akut" : data.jenis == "2" ? "Kronis" : "";
      if (type) parts.push(`Jenis: ${type}`);
    }

    return parts.join(", ") + ";";
  }

  private static setImt(setBb: number, setTb: number) {
    if (setBb < 1 || setTb < 1) return "0";
    const v = Number((setBb / Math.pow(setTb / 100, 2)).toFixed(2));
    return v.toFixed(2);
  }

  // ==================== Generators ====================

  public static generateFromRanap(ases: any): SoapData {
    if (!ases) return { s: "", o: "", a: "", p: "" };

    let s = "";
    if (ases.keluhan_utama) s += `Keluhan Utama: ${ases.keluhan_utama}\r\n`;
    if (ases.riwayat_penyakit_sekarang)
      s += `Riwayat Penyakit Sekarang: ${ases.riwayat_penyakit_sekarang}\r\n`;
    if (ases.riwayat_penyakit_dahulu)
      s += `Riwayat Penyakit Dahulu: ${ases.riwayat_penyakit_dahulu}\r\n`;
    if (ases.riwayat_penyakit_keluarga)
      s += `Riwayat Penyakit Keluarga: ${ases.riwayat_penyakit_keluarga}\r\n`;

    let o = "";
    if (ases.tanda_vital) o += this.formatTtv(ases.tanda_vital) + "\r\n";
    if (ases.antropometri)
      o += this.formatAntropometri(ases.antropometri) + "\r\n";

    if (ases.pemeriksaan_fisik) {
      const fisik = [];
      const mapKeys: { [key: string]: string } = {
        kepala: "kepala",
        leher: "leher",
        dada: "dada",
        jantung: "jantung",
        paru: "paru",
        perut: "perut",
        ektrimita_atas: "ekstremitas atas",
        ektrimita_bawah: "ekstremitas bawah",
        status_pubertas: "status pubertas",
        genital: "genital",
        tubuh_belakang: "tubuh belakang"
      };

      for (const [key, value] of Object.entries(ases.pemeriksaan_fisik)) {
        if (value && mapKeys[key]) {
          fisik.push(`${mapKeys[key]}: ${value}`);
        }
      }
      o += fisik.join("\r\n");
    }

    const penunjangText = ases.penunjang ? String(ases.penunjang).trim() : "";
    if (penunjangText) {
      if (o && !o.endsWith("\r\n")) o += "\r\n";
      o += `Pemeriksaan Penunjang: ${penunjangText};`;
    }

    return {
      s: s,
      o: o,
      a: ases.diagnosis || "",
      p: ases.terapi || ""
    };
  }

  public static generateFromRalan(
    asesmen: any,
    fisik: any,
    profesi: string // "Dokter" or "Perawat"
  ): SoapData {
    if (!asesmen) return { s: "", o: "", a: "", p: "" };

    // --- Subjective ---
    let s = "";
    if (profesi == "Dokter") {
      if (asesmen["keluhan_utama_medis"])
        s += asesmen["keluhan_utama_medis"] + "\n";
      if (asesmen["riwayat_penyakit_sekarang"])
        s += asesmen["riwayat_penyakit_sekarang"] + "\n";
    } else {
      if (asesmen["anamnesis"]?.keluhan)
        s += "Keluhan: " + asesmen["anamnesis"].keluhan + "\n";
      if (asesmen["riwayat_perawatan_sebelum"])
        s +=
          "Riwayat perawatan sebelumnya: " +
          asesmen["riwayat_perawatan_sebelum"] +
          "\n";

      const psyId = asesmen["anamnesis"]?.status_psikologis_id;
      if (psyId && psyId != "0") {
        const map: any = {
          "1": "Cemas Ringan",
          "2": "Cemas Sedang",
          "3": "Cemas Berat"
        };
        const status =
          map[psyId] ||
          `Lainnya, ${asesmen["anamnesis"].status_psikologis_text}`;
        s += `Status Psikologi: ${status}`;
      }
    }

    // --- Objective ---
    // Determine best data source (Asesmen Awal or Pemeriksaan Fisik)
    const dataSource = asesmen || fisik;
    let o = "";

    if (dataSource) {
      const tv = dataSource["tanda_vital"];
      const antro = dataSource["antropometri"];
      const nyeri = dataSource["nyeri"];
      const fung = dataSource["fungsional"];

      if (tv) o += this.formatTtv(tv) + "\n";
      if (antro) o += this.formatAntropometri(antro) + "\n";

      if (fung) {
        const parts: string[] = [];
        if (fung.alat_bantu) parts.push(`Alat Bantu: ${fung.alat_bantu}`);
        if (fung.prothesa) parts.push(`Prothesa: ${fung.prothesa}`);
        if (fung.cacat_tubuh) parts.push(`Cacat Tubuh: ${fung.cacat_tubuh}`);
        if (fung.adl)
          parts.push(`ADL: ${fung.adl == "0" ? "Mandiri" : "Dibantu"}`);
        if (parts.length) o += parts.join(", ") + ";\n";
      }

      // For nyeri, we pass the specific fallback logic
      const fallbackNyeri = asesmen && asesmen.nyeri ? null : fisik?.nyeri;
      if (nyeri || fallbackNyeri) o += this.formatNyeri(nyeri, fallbackNyeri);

      // Free-text yang diketik dokter di Asesmen Awal Ralan (Status Lokalis +
      // Pemeriksaan Penunjang). Tanpa ini, OBJECTIVE kosong untuk encounter
      // yang dokter saja tanpa input perawat (TTV/antropometri/nyeri).
      const lokalisCatatan = asesmen?.status_lokalis?.catatan
        ? String(asesmen.status_lokalis.catatan).trim()
        : "";
      if (lokalisCatatan) {
        if (o && !o.endsWith("\n")) o += "\n";
        o += `Status Lokalis: ${lokalisCatatan};\n`;
      }
      const penunjangText = asesmen?.penunjang
        ? String(asesmen.penunjang).trim()
        : "";
      if (penunjangText) {
        if (o && !o.endsWith("\n")) o += "\n";
        o += `Pemeriksaan Penunjang: ${penunjangText};`;
      }
    }

    // --- Assessment & Plan ---
    const a =
      profesi == "Dokter"
        ? asesmen["diagnosis_medis"]
        : asesmen["diagnosis_perawat"];
    const p =
      profesi == "Dokter"
        ? asesmen["tindakan_medis"]
        : asesmen["tindakan_perawat"];

    return { s, o, a, p };
  }

  public static generateFromGadar(
    medis: any,
    primer: any,
    perawat: any
  ): SoapData {
    const anamnesis = medis?.content?.anamnesis;
    const ttvData = primer?.content?.tanda_vital;
    const fisik = medis?.content?.fisik;
    const obgyn = perawat?.content?.riwayatObstetri;

    // --- Subjective ---
    const subjParts: string[] = [];
    if (anamnesis?.keluhanUtama)
      subjParts.push(`Keluhan: ${anamnesis.keluhanUtama}`);
    if (anamnesis?.riwayatPenyakitSekarang)
      subjParts.push(
        `Riwayat penyakit sekarang: ${anamnesis.riwayatPenyakitSekarang}`
      );
    if (anamnesis?.riwayatPenyakitDahulu)
      subjParts.push(
        `Riwayat penyakit dahulu: ${anamnesis.riwayatPenyakitDahulu}`
      );

    // --- Objective ---
    const ttvParts: string[] = [];
    if (ttvData?.tensi)
      ttvParts.push(
        `TD: ${ttvData.tensi.sistole}/${ttvData.tensi.diastole} mmHg`
      );
    if (ttvData?.nadi) ttvParts.push(`HR: ${ttvData.nadi} x/menit`);
    if (ttvData?.nafas) ttvParts.push(`RR: ${ttvData.nafas} x/menit`);
    if (ttvData?.suhu) ttvParts.push(`SB: ${ttvData.suhu} *C`);
    if (ttvData?.saturasi) ttvParts.push(`SpO2: ${ttvData.saturasi}%`);

    const fisikMap: { [key: string]: string } = {
      kepala: "Kepala",
      leher: "Leher",
      dada: "Dada",
      paru: "Paru",
      jantung: "Jantung",
      perut: "Perut",
      genital: "Genital",
      ektrimitaAtas: "Ektrimitas Atas",
      ektrimitaBawah: "Ektrimitas Bawah",
      tubuhBelakang: "Tubuh Belakang"
    };

    const fisikParts = Object.keys(fisikMap)
      .filter(key => fisik && fisik[key])
      .map(key => `${fisikMap[key]}: ${fisik[key]}`);

    const obgynParts: string[] = [];
    if (obgyn?.tfu) obgynParts.push(`TFU: ${obgyn.tfu} cm`);
    if (obgyn?.tbj) obgynParts.push(`TBJ: ${obgyn.tbj} gr`);
    if (obgyn?.djj) obgynParts.push(`DJJ: ${obgyn.djj} x/mnt`);
    if (obgyn?.lla) obgynParts.push(`LLA: ${obgyn.lla} cm`);
    if (obgyn?.vt) obgynParts.push(`VT: ${obgyn.vt}`);
    if (obgyn?.his) obgynParts.push(`HIS: ${obgyn.his}`);

    const o = [
      ttvParts.join("; "),
      fisikParts.join("; "),
      obgynParts.join("; ")
    ]
      .filter(Boolean)
      .join("\r\n")
      .trim();
    const s = subjParts.join("\r\n").trim();
    const a = medis?.content?.diagnosis?.kerja || "";
    const p = medis?.content?.terapi?.terapi || "";

    return { s, o, a, p };
  }

  // ==================== Keperawatan (Ranap) ====================

  private static has(v: any): boolean {
    return v !== null && v !== undefined && String(v).trim() !== "";
  }

  private static join(parts: any[], sep: string): string {
    return parts
      .filter(p => this.has(p))
      .map(p => String(p).trim())
      .join(sep)
      .trim();
  }

  private static row(label: string, value: any): string {
    if (!this.has(value)) return "";
    const v = String(value).trim();
    return v.indexOf("\n") >= 0 ? `${label}:\r\n${v}` : `${label}: ${v}`;
  }

  private static formatTtvKeperawatan(tv: any): string {
    if (!tv) return "";
    const parts: string[] = [];

    if (this.has(tv.sistole) && this.has(tv.diastole))
      parts.push(`TD: ${tv.sistole}/${tv.diastole} mmHg`);
    if (this.has(tv.nadi)) parts.push(`Nadi: ${tv.nadi} x/menit`);
    if (this.has(tv.nafas)) parts.push(`Nafas: ${tv.nafas} x/menit`);
    if (this.has(tv.suhu)) parts.push(`Suhu: ${tv.suhu} °C`);
    if (this.has(tv.saturasi_oksigen))
      parts.push(`SpO2: ${tv.saturasi_oksigen}%`);

    return parts.join("; ");
  }

  private static formatAntropometriKeperawatan(antro: any): string {
    if (!antro) return "";
    const parts: string[] = [];

    if (this.has(antro.bb)) parts.push(`BB: ${antro.bb} kg`);
    if (this.has(antro.tb)) parts.push(`TB: ${antro.tb} cm`);

    let imt = this.has(antro.imt) ? String(antro.imt).trim() : "";
    if (imt === "" && this.has(antro.bb) && this.has(antro.tb)) {
      imt = this.setImt(Number(antro.bb), Number(antro.tb));
    }
    if (imt !== "" && imt !== "0") parts.push(`IMT: ${imt}`);

    return parts.join("; ");
  }

  private static formatKesadaran(content: any): string {
    const gcs = content && content.gcs ? content.gcs : {};
    const komp: string[] = [];

    if (this.has(gcs.e)) komp.push(`E${gcs.e}`);
    if (this.has(gcs.v)) komp.push(`V${gcs.v}`);
    if (this.has(gcs.m)) komp.push(`M${gcs.m}`);

    let total: number = null;
    if (this.has(gcs.total)) {
      total = Number(gcs.total);
    } else if (komp.length === 3) {
      total = Number(gcs.e) + Number(gcs.v) + Number(gcs.m);
    }

    let gcsText = "";
    if (komp.length) {
      gcsText = `GCS ${komp.join(" ")}`;
      if (total !== null && !isNaN(total)) gcsText += ` = ${total}`;
    }

    const kesadaran =
      content && this.has(content.kesadaran)
        ? String(content.kesadaran).trim()
        : "";

    if (kesadaran !== "" && gcsText !== "")
      return `Kesadaran: ${kesadaran} (${gcsText})`;
    if (kesadaran !== "") return `Kesadaran: ${kesadaran}`;
    return gcsText !== "" ? `Kesadaran: ${gcsText}` : "";
  }

  private static formatNyeriBerskor(nyeri: any): string {
    if (!nyeri) return "";
    if (nyeri.ada !== true) return "Nyeri: Tidak ada keluhan nyeri";

    const metode = this.has(nyeri.skala_method)
      ? String(nyeri.skala_method).trim()
      : "";
    const skor = this.has(nyeri.skor) ? Number(nyeri.skor) : null;

    let label = this.has(nyeri.interpretasi)
      ? String(nyeri.interpretasi).trim()
      : "";
    if (label === "") {
      const band = bandNyeri(metode, skor);
      label = band ? judulBandNyeri(band) : "";
    }

    let kepala = "Nyeri";
    if (metode !== "") kepala += ` (${metode})`;
    kepala +=
      skor === null || isNaN(skor) ? ": skor belum dinilai" : `: skor ${skor}`;
    if (label !== "") kepala += ` — ${label}`;

    const komponen = (Array.isArray(nyeri.komponen) ? nyeri.komponen : [])
      .filter((k: any) => k && k.ref_id !== null && k.ref_id !== undefined)
      .map(
        (k: any) =>
          `${this.has(k.ref_group_title) ? k.ref_group_title : "-"}: ${
            this.has(k.ref_title) ? k.ref_title : "-"
          }${this.has(k.skor) ? ` (${k.skor})` : ""}`
      );

    return this.join(
      [
        kepala,
        this.row("Jenis", nyeri.jenis),
        this.join(
          [
            this.row("P (Pencetus)", nyeri.p),
            this.row("Q (Kualitas)", nyeri.q),
            this.row("R (Lokasi/Penjalaran)", nyeri.r),
            this.row("T (Pola Waktu)", nyeri.t)
          ],
          "; "
        ),
        komponen.length ? `Rincian: ${komponen.join("; ")}` : ""
      ],
      "\r\n"
    );
  }

  private static formatRpdTerstruktur(rpd: any): string {
    if (!rpd) return "";

    const status = String(rpd.status_rpd || "");
    if (status === "tidak_ada") return "Disangkal";
    if (status === "tidak_diketahui") {
      return this.has(rpd.alasan_unk)
        ? `Tidak diketahui (${String(rpd.alasan_unk).trim()})`
        : "Tidak diketahui";
    }

    const baris = (Array.isArray(rpd.entries) ? rpd.entries : [])
      .map((e: any) => {
        const nama = this.join([e && e.icd10_display, e && e.icd10_code], " ");
        if (nama === "") return "";
        const ket = this.join(
          [
            e && this.has(e.onset_display) ? `onset ${e.onset_display}` : "",
            e && e.clinical_status,
            e && e.catatan
          ],
          ", "
        );
        return ket === "" ? nama : `${nama} (${ket})`;
      })
      .filter((b: string) => b !== "");

    if (baris.length === 1) return baris[0];
    return baris.map((b: string) => `- ${b}`).join("\r\n");
  }

  private static formatAlergi(alergi: any): string {
    const status = String((alergi && alergi.status) || "");
    if (status === "ada") {
      return alergi && this.has(alergi.detail)
        ? `Ada, ${String(alergi.detail).trim()}`
        : "Ada";
    }
    if (status === "tidak") return "Tidak ada";
    if (status === "tidak_diketahui") return "Tidak diketahui";
    return "";
  }

  private static formatRekonsiliasiObat(rekon: any): string {
    const status = String((rekon && rekon.status) || "");
    if (status === "tidak") return "Tidak menggunakan obat";
    if (status === "tidak_diketahui") return "Tidak diketahui";
    if (status !== "menggunakan") return "";

    const baris = (Array.isArray(rekon.daftar) ? rekon.daftar : [])
      .map((o: any) =>
        this.join(
          [o && o.nama, o && o.dosis, o && o.frekuensi, o && o.rute],
          " - "
        )
      )
      .filter((b: string) => b !== "")
      .map((b: string) => `- ${b}`);

    return baris.length
      ? `Menggunakan obat:\r\n${baris.join("\r\n")}`
      : "Menggunakan obat";
  }

  private static formatKebiasaan(riwayat: any): string {
    const r = riwayat || {};
    const item = (label: string, val: any, ket: any) => {
      if (!this.has(val)) return "";
      const v = String(val).trim();
      const teks = v.charAt(0).toUpperCase() + v.slice(1);
      return this.has(ket)
        ? `${label}: ${teks} (${String(ket).trim()})`
        : `${label}: ${teks}`;
    };

    return this.join(
      [
        item("Merokok", r.sosial_rokok, r.sosial_rokok_ket),
        item("Alkohol", r.sosial_alkohol, r.sosial_alkohol_ket),
        item("NAPZA", r.sosial_napza, r.sosial_napza_ket)
      ],
      "; "
    );
  }

  private static formatMasalahKeperawatan(list: any): string {
    const items = (Array.isArray(list) ? list : [])
      .map((m: any) => (m === null || m === undefined ? "" : String(m).trim()))
      .filter((m: string) => m !== "");

    return items.map((m, i) => `${i + 1}. ${m}`).join("\r\n");
  }

  public static generateFromRanapKeperawatan(ases: any, rpd?: any): SoapData {
    const content = ases ? ases.content : null;
    if (!content) return { s: "", o: "", a: "", p: "" };

    const riwayat = content.riwayat || {};
    const rpdTeks = this.formatRpdTerstruktur(rpd) || riwayat.rpd;

    const s = this.join(
      [
        this.row("Keluhan Utama", content.keluhan_utama),
        this.row("Riwayat Penyakit Sekarang", riwayat.rps),
        this.row("Riwayat Penyakit Dahulu", rpdTeks),
        this.row("Riwayat Penyakit Keluarga", riwayat.rpk),
        this.row("Riwayat Alergi", this.formatAlergi(content.alergi)),
        this.row(
          "Obat yang sedang digunakan",
          this.formatRekonsiliasiObat(content.rekonsiliasi_obat)
        ),
        this.row("Kebiasaan", this.formatKebiasaan(riwayat))
      ],
      "\r\n"
    );

    const o = this.join(
      [
        this.row("Keadaan Umum", content.keadaan_umum),
        this.formatKesadaran(content),
        this.formatTtvKeperawatan(content.tanda_vital),
        this.formatAntropometriKeperawatan(content.antropometri),
        this.formatNyeriBerskor(content.nyeri)
      ],
      "\r\n"
    );

    return {
      s,
      o,
      a: this.formatMasalahKeperawatan(content.masalah_keperawatan),
      p: ""
    };
  }

  public static formatTtvMonitor(ttv: any): string {
    if (!ttv) return "";

    const isi = this.formatTtvKeperawatan({
      sistole: ttv.sistole,
      diastole: ttv.diastole,
      nadi: ttv.frekuensi_nadi,
      nafas: ttv.frekuensi_nafas,
      suhu: ttv.suhu_badan,
      saturasi_oksigen: ttv.saturasi_oksigen
    });

    if (isi === "") return "";

    const jam = this.has(ttv.waktu_periksa)
      ? moment(ttv.waktu_periksa).format("DD/MM/YYYY HH:mm")
      : "";

    return jam === "" ? `TTV: ${isi}` : `TTV (${jam}): ${isi}`;
  }

  public static replaceTtvLine(objective: string, baris: string): string {
    if (!this.has(baris)) return objective || "";

    const teks = objective || "";
    if (teks.trim() === "") return baris;

    const eol = teks.indexOf("\r\n") >= 0 ? "\r\n" : "\n";
    const larik = teks.split(/\r?\n/);
    const idx = larik.findIndex(b => /^\s*(TTV\b|TD\s*:)/i.test(b));

    if (idx >= 0) {
      larik[idx] = baris;
      return larik.join(eol);
    }

    return teks.replace(/\s+$/, "") + eol + baris;
  }

  public static generateFromPreviousSoap(soap: any): SoapData {
    return {
      s: (soap && soap.subjective) || "",
      o: (soap && soap.objective) || "",
      a: (soap && soap.assesment) || "",
      p: (soap && soap.plan) || ""
    };
  }
}
